<template>
    <section ref="section" class="slice slice-media slice-media--demo" data-testid="media-demo-slice" aria-label="media">
        <div class="slice-content text-left">
            <div class="slice-media--demo__inner" :class="{'slice-media--demo__inner--reverse': slice.primary.reverse}">
                <div class="slice-media--demo__text">
                    <div>
                        <div v-if="slice.primary.tag" class="slice-media--demo__tag" data-testid="media-demo-tag">
                            {{ slice.primary.tag }}
                        </div>
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.title)"
                            :field="slice.primary.title"
                            class="slice-title"
                            data-testid="media-demo-title"
                        />
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.description)"
                            :field="slice.primary.description"
                            data-testid="media-demo-description"
                        />
                        <div v-if="isFilled.link(slice.primary.cta)" class="slice-media--demo__cta">
                            <NuxtLink
                                :to="$withLocalePrefix(slice.primary.cta.url ?? '')"
                                data-component-source="demo-popup"
                                data-testid="media-demo-cta"
                            >
                                {{ slice.primary.cta.text }}
                            </NuxtLink>
                        </div>
                    </div>
                </div>
                <div class="slice-media--demo__media" data-testid="media-demo-media" @click="showDialog()">
                    <PrismicImage
                        v-if="isFilled.image(slice.primary.coverImage)"
                        class="slice-media--demo__image"
                        :field="slice.primary.coverImage"
                        data-testid="media-demo-cover-image"
                    />
                    <div class="slice-media--demo__cover">
                        <div class="slice-media--demo__overlay" :class="overlayClass" />
                        <div class="slice-media--demo__cover__content">
                            <MaltImg width="80" :src="`/cms-front/demo-click.svg?vh=dc6686`" :alt="slice.primary.coverText ?? ''" />
                            <p data-testid="media-demo-cover-text">{{ slice.primary.coverText }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <VJoyDialog ref="dialog" class="slice-media--demo__dialog" data-testid="media-demo-dialog">
        <template #dialog-body>
            <iframe
                v-if="slice.primary.demoUrl"
                :src="slice.primary.demoUrl"
                frameborder="0"
                class="slice-media--demo__iframe"
                data-testid="media-demo-iframe"
                @load="onDemoLoaded"
            />
            <div v-if="!isDemoLoaded" class="slice-media--demo__dialog__loader">
                <VJoySpinner />
            </div>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {MediaSlice} from '@prismicio-types';
    import {MaltImg} from '#components';
    import {useComponentTracking} from '#imports';
    import {VJoyDialog, VJoySpinner} from '@maltjoy/core-vue';

    type MediaDemoInZone = Extract<MediaSlice, {variation: 'mediaDemo'}>;

    const props = defineProps(getSliceComponentProps<MediaDemoInZone>());

    const section = useTemplateRef('section');
    const dialog = useTemplateRef('dialog');
    const isDemoLoaded = ref(false);

    const overlayClass = computed(() => {
        const coverColor = props.slice.primary.coverColor;
        const color = coverColor === 'gradient' ? 'ai-gradient' : coverColor;
        return `slice-media--demo__overlay--${color}`;
    });

    function onDemoLoaded() {
        setTimeout(() => (isDemoLoaded.value = true), 1000);
    }

    async function showDialog() {
        isDemoLoaded.value = false;
        await dialog.value?.show();
    }

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-media--demo {
        .slice-content {
            @include mq.screen_lg(min) {
                padding-top: 120px;
                padding-bottom: 120px;
            }
        }

        .slice-title {
            line-height: var(--joy-line-height-small);
            margin-bottom: var(--joy-core-spacing-3);
        }

        &__inner {
            display: flex;
            flex-direction: column-reverse;
            position: relative;
            text-align: left;
            gap: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                flex-direction: row;
                align-items: center;
                gap: 0;

                &--reverse {
                    flex-direction: row-reverse;
                }

                > div {
                    width: 50%;
                }
            }
        }

        &__cta {
            margin-top: var(--joy-core-spacing-4);

            a {
                color: var(--joy-color-primary-50);
                font-weight: var(--joy-font-weight-bold);

                &:hover {
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }
            }
        }

        &__text {
            padding-left: 0;
            padding-right: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                .slice-media--demo__inner--reverse & {
                    padding-left: var(--joy-core-spacing-8);
                    padding-right: 0;
                }
            }

            @include mq.screen_lg(min) {
                padding-right: var(--joy-core-spacing-12);

                .slice-media--demo__inner--reverse & {
                    padding-left: var(--joy-core-spacing-12);
                }
            }
        }

        &__media {
            position: relative;
            border-radius: var(--joy-core-radius-4);
            overflow: hidden;
            box-shadow: var(--joy-core-elevation-3);
            cursor: pointer;
        }

        &__cover,
        &__overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }

        &__cover {
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;

            &__content {
                pointer-events: none;
                position: relative;
                color: var(--joy-color-neutral-0);
                max-width: 400px;
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-800);

                p {
                    color: var(--joy-color-neutral-0);
                    font-family: var(--joy-font-family-title);
                    margin-top: var(--joy-core-spacing-4);
                }
            }
        }

        &__overlay {
            cursor: pointer;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            opacity: 0.85;

            &--red {
                background-color: var(--joy-color-primary-50);
            }

            &--ai-gradient {
                background-image: var(--joy-color-ai-gradient-30);
            }

            &--blue {
                background-color: var(--joy-color-brand-secondary-30);
            }

            &:hover {
                opacity: 0.9;
            }
        }

        &__image {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            filter: blur(3px);
        }

        &__tag {
            font-size: var(--joy-font-size-primary-200);
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-neutral-40);
            font-family: var(--joy-font-family-base);
            letter-spacing: var(--joy-font-letter-spacing-tight);
            text-transform: uppercase;
            user-select: none;
            margin-bottom: var(--joy-core-spacing-5);
        }

        &__dialog {
            &__loader {
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;

                .joy-spinner {
                    --spinner-size: 56px;
                }
            }

            .joy-dialog__overlay {
                background-color: rgba(0, 0, 0, 0.8);
            }

            .joy-dialog__inner {
                width: auto;
                padding: 0;
            }

            .joy-dialog--header {
                margin-bottom: var(--joy-core-spacing-9);

                @include mq.screen_sm(min) {
                    display: none;
                }
            }

            .joy-dialog--body {
                max-width: none;
                max-height: none;
            }

            iframe {
                display: block;
                width: calc(100vw - 40px);
                max-width: 1400px;
                max-height: calc(100vh - 40px);
                aspect-ratio: 16 / 9;
            }
        }
    }
</style>
